import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ArticleHeader from "@/components/article/ArticleHeader";
import ArticleContent from "@/components/article/ArticleContent";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import DynamicTitle from "@/components/DynamicTitle";
import SEOStructuredData from "@/components/SEOStructuredData";
import SEOMetaTags from "@/components/SEOMetaTags";

interface ArticleData {
  id: string;
  title: string;
  content: string;
  date: string;
  topic: string;
  image_url: string | null;
  category: {
    name: string;
  } | null;
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleData>({
    id: "",
    title: "",
    content: "",
    date: "",
    topic: "",
    image_url: null,
    category: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [readingTime, setReadingTime] = useState<number>(0);
  
  useEffect(() => {
    fetchArticle();
  }, [slug]);
  
  useEffect(() => {
    if (article.content) {
      const textContent = article.content.replace(/<[^>]*>/g, ' ');
      const wordCount = textContent.trim().split(/\s+/).length;
      const readingTimeMinutes = Math.ceil(wordCount / 200);
      setReadingTime(Math.max(1, readingTimeMinutes));
    }
  }, [article.content]);
  
  const fetchArticle = async () => {
    try {
      if (!slug) throw new Error("Article slug is missing");
      
      const { data, error } = await supabase
        .from("articles")
        .select(`
          id,
          title,
          content,
          date,
          topic,
          image_url,
          category:category_id(name)
        `)
        .eq("slug", slug)
        .eq("publication_state", "published")
        .single();
      
      if (error) {
        console.error("Error fetching article:", error);
        throw error;
      }
      
      if (!data) {
        navigate("/not-found");
        return;
      }
      
      console.log("Article data loaded:", data);
      
      if (data.image_url) {
        console.log("Image URL from database:", data.image_url);
      } else {
        console.log("No image URL available for this article");
      }
      
      setArticle(data);
    } catch (error) {
      console.error("Error in fetchArticle:", error);
      toast.error("Failed to load article");
      navigate("/not-found");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
        <Header />
        <LoadingSpinner message="Loading article..." />
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
      <SEOMetaTags
        title={article.title}
        description={article.content.replace(/<[^>]*>/g, ' ').substring(0, 160)}
        keywords={`${article.topic}, ${article.category?.name || 'AI'}, artificial intelligence, Aram AI, tech, entrepreneurship`}
        canonicalUrl={`https://tezzarida.io/article/${slug}`}
        ogType="article"
        ogImage={article.image_url || "https://tezzarida.io/social-preview.png"}
      />
      <DynamicTitle 
        title={article.title}
        description={article.content.replace(/<[^>]*>/g, ' ').substring(0, 160)}
        canonicalUrl={`https://tezzarida.io/article/${slug}`}
      />
      <SEOStructuredData
        type="article"
        title={article.title}
        description={article.content.replace(/<[^>]*>/g, ' ').substring(0, 160)}
        url={`https://tezzarida.io/article/${slug}`}
        image={article.image_url || "https://tezzarida.io/social-preview.png"}
        datePublished={article.date}
        dateModified={article.date}
        category={article.category?.name}
      />
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <article className="glass-panel rounded-lg overflow-hidden">
          <div className="px-8 py-6">
            <ArticleHeader 
              title={article.title}
              topic={article.topic}
              date={article.date}
              readingTime={readingTime}
              category={article.category}
              imageUrl={article.image_url}
            />
            <ArticleContent content={article.content} />
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
